// Clean text pasted/typed from the web or word processors (MS Word especially)
// before it's parsed or stored. Two concerns:
//   1. Invisible/odd characters that break display wrapping, search, and matching --
//      non-breaking & exotic spaces, zero-width chars, soft hyphens, BOM.
//   2. "Smart" typography (curly quotes, ellipsis) that makes search inconsistent.
//
// All done with explicit regex (no String.normalize -- kept Hermes-safe, matching
// parseRecipeText's accent folding).

// Unicode space variants -> a normal space. Covers nbsp (U+00A0), narrow nbsp
// (U+202F), the en/em/thin/hair spaces (U+2000-U+200A), figure/ideographic spaces.
const UNICODE_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;
// Zero-width & formatting chars that are invisible but corrupt search/wrapping:
// ZWSP, ZWNJ, ZWJ, word-joiner, BOM, soft hyphen (U+00AD).
const ZERO_WIDTH = /[\u200B-\u200D\u2060\uFEFF\u00AD]/g;
// Smart quotes -> straight. Single: U+2018 U+2019 U+201B, prime U+2032. Double:
// U+201C U+201D U+201F, double-prime U+2033.
const SMART_SINGLE = /[\u2018\u2019\u201B\u2032]/g;
const SMART_DOUBLE = /[\u201C\u201D\u201F\u2033]/g;

// Light clean for any stored field value. Normalises line endings, converts odd
// spaces to normal ones, strips zero-width/soft-hyphen chars, collapses runs of
// horizontal whitespace, and trims -- while PRESERVING newlines (for notes/steps)
// and legitimate typography like curly quotes (iOS inserts these automatically).
export function normalizeField(text: string): string {
  if (!text) return text;
  return text
    .replace(/\r\n?/g, "\n") // CRLF / CR -> LF
    .replace(UNICODE_SPACES, " ")
    .replace(ZERO_WIDTH, "")
    .replace(/[^\S\n]+/g, " ") // collapse horizontal whitespace runs (not newlines)
    .replace(/[^\S\n]*\n[^\S\n]*/g, "\n") // trim spaces around newlines
    .trim();
}

// Aggressive clean for pasted/imported recipe BLOCKS (the Quick Paste / import
// path), where Word artifacts are common and we're about to parse structure. Builds
// on normalizeField and additionally straightens smart quotes and ellipsis so the
// parsed/stored text is consistent. Dashes are left alone (en-dash is meaningful in
// quantity ranges, and the parsers already handle both forms).
export function normalizePastedText(text: string): string {
  if (!text) return text;
  return normalizeField(
    text
      .replace(SMART_SINGLE, "'")
      .replace(SMART_DOUBLE, '"')
      .replace(/\u2026/g, "..."), // ellipsis -> ...
  );
}
